import React, { useMemo } from 'react';

import SimpleTableWithSorting, {
  generateColumn,
} from 'app/components/common/SimpleTableWithSorting';
import { roleNamesById } from 'enums';
import { TUser, TUserRestaurant } from 'types';
import { formatUserTableShortDate } from 'utils/user-datetime-display';

type TMinimalRestaurant = {
  id: number;
  name: string;
};

interface UsersRestaurantsTableProps {
  users: TUser[];
  userRestaurants: TUserRestaurant[];
  restaurants: TMinimalRestaurant[];
  showCreatedAt?: boolean;
  showUpdatedAt?: boolean;
}

type TUserRestaurantWithUserFields = TUserRestaurant & {
  userName: string;
  userEmail: string;
  roleAsString: string;
  restaurant: string;
};

export const UsersRestaurantsTable: React.FC<UsersRestaurantsTableProps> = ({
  restaurants,
  users,
  userRestaurants,
  showCreatedAt = false,
  showUpdatedAt = false,
}) => {
  const formattedUserRestaurants: TUserRestaurantWithUserFields[] =
    useMemo(() => {
      const usersById: Record<number, TUser> = users.reduce(
        (acc: Record<number, TUser>, user) => {
          acc[user.id] = user;
          return acc;
        },
        {}
      );
      const restaurantsById: Record<number, TMinimalRestaurant> =
        restaurants.reduce(
          (acc: Record<number, TMinimalRestaurant>, restaurant) => {
            acc[restaurant.id] = restaurant;
            return acc;
          },
          {}
        );

      return userRestaurants.map(({ user_id, role_id, ...rest }) => {
        const user: TUser | undefined = usersById[user_id];
        const restaurant: TMinimalRestaurant | undefined =
          restaurantsById[rest.restaurant_id];
        return {
          ...rest,
          user_id,
          role_id,
          userName: `${user?.name} (${user?.id})`,
          userEmail: user?.email || '-',
          roleAsString: roleNamesById[role_id]
            ? `${roleNamesById[role_id]} (${role_id})`
            : '-',
          restaurant: restaurant
            ? `${restaurant?.name} (${restaurant.id})`
            : 'Brak restauracji',
        };
      });
    }, [restaurants, users, userRestaurants]);

  const columns = useMemo(
    () => [
      generateColumn('id'),
      generateColumn('userName', {
        customHeader: 'Użytkownik (imię, ID)',
      }),
      generateColumn('userEmail', {
        customHeader: 'Email',
      }),
      generateColumn('roleAsString', {
        customHeader: 'Rola',
      }),
      generateColumn('restaurant', {
        customHeader: 'Restauracja (nazwa, ID)',
      }),
      ...(showCreatedAt
        ? [
            generateColumn('created_at', {
              customHeader: 'Utworzono',
              customCellFormatter: (props) => (
                <div>{formatUserTableShortDate(props.getValue())}</div>
              ),
            }),
          ]
        : []),
      ...(showUpdatedAt
        ? [
            generateColumn('updated_at', {
              customHeader: 'Zaktualizowano',
              customCellFormatter: (props) => (
                <div>{formatUserTableShortDate(props.getValue())}</div>
              ),
            }),
          ]
        : []),
    ],
    [showCreatedAt, showUpdatedAt]
  );

  return (
    <SimpleTableWithSorting
      columns={columns}
      data={formattedUserRestaurants}
      hasFilters
      pageSize={50}
    />
  );
};

export default UsersRestaurantsTable;
